import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { Table, Chip, Button, ChipLabel } from "@heroui/react";
import { ChevronDown, ChevronUp, CalendarX, Filter, Star, Play, Users, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { normalizeMeets } from "@/utils/meets";
import { getRelativeDateTimeLabel, getReadableTimeDiff } from "@/utils/dates";
import MemberAvatar from "@/components/members/member-avatar";
import TablePagination from "./table-pagination";
import SkeletonTable from "./skeleton-table";
import TableFilterButton from "./table-filter-button";
import MultiSelectFilterSurface from "../desktop/muli-select-filter-surface";
import SearchInput from "../search-input";

const RECORDING_ICON_COLOR_CLASSES = [
  "bg-danger/15 text-danger",
  "bg-success/15 text-success",
  "bg-warning/15 text-warning",
  "bg-accent/15 text-accent",
];

const getRecordingIconColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 4) - hash);
  return RECORDING_ICON_COLOR_CLASSES[Math.abs(hash) % RECORDING_ICON_COLOR_CLASSES.length];
};

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(String(row.getValue(columnId) ?? ""), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

const isWithinPastDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - target) / 86400000);
  return diffDays >= 0 && diffDays < days;
};

const TABS = [
  { key: "all", label: "All Recordings" },
  { key: "thisWeek", label: "This Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "starred", label: "Starred" },
];

const FILTER_KEYS = ["participant", "recordedby"];

const RecordingsTable = ({ recordings, className, selectedRecordingId, onSelectRecording, isLoading }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([{ id: "date", desc: true }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const pageSize = 8;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("filter") ?? "all";
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      prev.set("filter", tab);
      return prev;
    });
    setPageIndex(0);
  };
  const participantFilter = searchParams.getAll("participant");
  const recordedByFilter = searchParams.getAll("recordedby");
  const participantKey = participantFilter.join(",");
  const recordedByKey = recordedByFilter.join(",");
  const activeFilterCount = participantFilter.length + recordedByFilter.length;

  const handleClearFilters = () => {
    setSearchParams((prev) => {
      FILTER_KEYS.forEach((key) => prev.delete(key));
      return prev;
    });
  };

  const participantOptions = useMemo(() => {
    const byId = new Map();
    for (const record of recordings) {
      for (const member of record.members ?? []) {
        const value = member.member_email;
        if (!value || byId.has(value)) continue;
        byId.set(value, { value, label: member.member_fullname || member.member_email || "Unknown" });
      }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [recordings]);

  const recordedByOptions = useMemo(() => {
    const byId = new Map();
    for (const record of recordings) {
      const value = record.created_by?.user_id ?? record.created_by?.user_email;
      if (!value || byId.has(value)) continue;
      byId.set(value, { value, label: record.created_by?.user_email || "Unknown" });
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [recordings]);

  const normalizedRecordings = useMemo(() => {
    return normalizeMeets(recordings).map((rec) => ({
      ...rec,
      isStarred: false,
      isThisWeek: isWithinPastDays(rec.meets_date, 7),
      isThisMonth: isWithinPastDays(rec.meets_date, 30),
    }));
  }, [recordings]);

  const filteredRecordings = useMemo(() => {
    let result = normalizedRecordings;
    if (participantFilter.length > 0) {
      const wanted = new Set(participantFilter);
      result = result.filter((rec) =>
        rec.members?.some((m) => wanted.has(m.member_email)),
      );
    }
    if (recordedByFilter.length > 0) {
      const wanted = new Set(recordedByFilter);
      result = result.filter(
        (rec) => wanted.has(rec.created_by?.user_id) || wanted.has(rec.created_by?.user_email),
      );
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedRecordings, participantKey, recordedByKey]);

  const tabCounts = useMemo(
    () => ({
      all: filteredRecordings.length,
      thisWeek: filteredRecordings.filter((r) => r.isThisWeek).length,
      thisMonth: filteredRecordings.filter((r) => r.isThisMonth).length,
      starred: 0,
    }),
    [filteredRecordings],
  );

  const tabFilteredRecordings = useMemo(() => {
    if (activeTab === "thisWeek") return filteredRecordings.filter((r) => r.isThisWeek);
    if (activeTab === "thisMonth") return filteredRecordings.filter((r) => r.isThisMonth);
    if (activeTab === "starred") return [];
    return filteredRecordings;
  }, [filteredRecordings, activeTab]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Recording",
        cell: ({ row }) => {
          const rec = row.original;
          const initials = (rec.title || "??").slice(0, 2).toUpperCase();
          return (
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "size-9 border rounded-full flex items-center justify-center text-xs font-semibold shrink-0",
                )}
              >
                {/* {initials} */}
                <Play className="fill-accent text-accent size-4" />
              </div>
              <div className="flex flex-col gap-2">
                <span className="font-semibold text-sm">{rec.title}</span>
                <div className="flex items-center gap-2">
                  <Chip variant="soft" color="accent">
                    <Chip.Label>Ai Summary</Chip.Label>
                  </Chip>

                  <div className="flex items-center gap-1">
                    <Users size={12} />

                    <span className="text-xs">{rec.members.length}</span>
                  </div>
                </div>
              </div>
              {rec.isStarred && <Star size={14} className="fill-warning text-warning shrink-0" />}
            </div>
          );
        },
      },
      {
        id: "loop",
        accessorFn: (rec) => rec.title,
        header: "Loop",
        cell: ({ row }) => {
          const rec = row.original;
          const initials = (rec.title || "??").slice(0, 2).toUpperCase();
          return (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "size-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0",
                  getRecordingIconColor(rec.shortCode || rec.title),
                )}
              >
                {initials}
              </div>
              <span className="font-medium">{rec.title}</span>
            </div>
          );
        },
      },
      {
        id: "date",
        accessorFn: (rec) => `${rec.meets_date} ${rec.start_time}`,
        header: "Date",
        cell: ({ row }) => {
          const rec = row.original;
          return <span className="text-sm">{getRelativeDateTimeLabel(rec.meets_date, rec.start_time)}</span>;
        },
      },
      {
        id: "duration",
        accessorFn: (rec) => rec.start_time,
        header: "Duration",
        enableSorting: false,
        cell: ({ row }) => {
          const rec = row.original;
          return (
            <span className="text-sm text-muted">{getReadableTimeDiff(rec.start_time, rec.end_time, "short")}</span>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: tabFilteredRecordings,
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    globalFilterFn: fuzzyFilter,
    state: { globalFilter, sorting, pagination: { pageIndex, pageSize } },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const totalRows = table.getFilteredRowModel().rows.length;
  const from = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(from + pageSize - 1, totalRows);

  // React Aria's Table requires selectedKeys to be a Set.
  const selectedKeysSet = useMemo(
    () => (selectedRecordingId ? new Set([selectedRecordingId]) : new Set()),
    [selectedRecordingId],
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {/* Toolbar: search + tabs + filter */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1 bg-default/40 rounded-xl p-1">
          {TABS.map((tab) => (
            <TableFilterButton
              key={tab.key}
              label={tab.label}
              count={tabCounts[tab.key]}
              isActive={activeTab === tab.key}
              onClick={() => {
                setActiveTab(tab.key);
              }}
            />
          ))}
        </div>

        <div>
          <Button
            size="md"
            variant="outline"
            className={cn("border rounded-full bg-surface", showFilters && "bg-accent/8 text-purple-foreground")}
            onPress={() => setShowFilters((prev) => !prev)}
          >
            <Filter className={cn(showFilters && "text-purple-foreground")} />
            Filter
            {activeFilterCount > 0 && (
              <Chip variant="soft" color="accent" size="sm">
                <Chip.Label>{activeFilterCount}</Chip.Label>
              </Chip>
            )}
            <div className={cn(showFilters ? "rotate-180" : "rotate-0")}>
              <ChevronDown className={cn(showFilters && "text-purple-foreground")} />
            </div>
          </Button>
        </div>
      </div>

        {showFilters && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-accent/30 bg-accent/5 p-3">
              <SearchInput term={globalFilter} onChange={setGlobalFilter} />
          <MultiSelectFilterSurface
            label="Participants"
            defaultLabel="Anyone"
            filterKey="participant"
            options={participantOptions}
            triggerClassName="min-w-48"
          />

          {/* <MultiSelectFilterSurface
            label="Recorded by"
            defaultLabel="Anyone"
            filterKey="recordedby"
            options={recordedByOptions}
            triggerClassName="min-w-48"
          /> */}

          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" className="ms-auto text-muted" onPress={handleClearFilters}>
              <X size={14} />
              Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <SkeletonTable />
      ) : (
        <Table.Root className="bg-surface">
          <Table className="bg-surface pt-3">
            <Table.ScrollContainer>
              <Table.Content
                aria-label="Recordings table"
                selectionMode="single"
                selectionBehavior="replace"
                selectedKeys={selectedKeysSet}
                onSelectionChange={(keys) => {
                  const [first] = keys;
                  if (first) onSelectRecording?.(first);
                }}
              >
                <Table.Header className="bg-surface pt-10">
                  {table.getHeaderGroups()[0].headers.map((header, i) => (
                    <Table.Column key={header.id} isRowHeader={i === 0} allowsSorting={header.column.getCanSort()}>
                      <div
                        className={cn(
                          "flex items-center text-sm font-semibold py-2 text-nowrap gap-1",
                          header.column.getCanSort() && "cursor-pointer select-none",
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ChevronUp size={13} />,
                          desc: <ChevronDown size={13} />,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </Table.Column>
                  ))}
                </Table.Header>

                <Table.Body
                  renderEmptyState={() => (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                      <div className="rounded-full bg-default/60 p-4">
                        <CalendarX className="size-6 text-muted" />
                      </div>
                      <div>
                        <p className="font-semibold">No recordings found</p>
                        <p className="text-sm text-muted">
                          {globalFilter ? `No results for "${globalFilter}"` : "Nothing in this view yet."}
                        </p>
                      </div>
                    </div>
                  )}
                >
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row
                      key={row.original.id}
                      id={row.original.id}
                      className={cn(
                        "cursor-pointer rounded-lg border-2 border-transparent"
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id} className="text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>

          <TablePagination table={table} pageSize={pageSize} totalRows={totalRows} noun="recordings" />
        </Table.Root>
      )}
    </div>
  );
};

export default RecordingsTable;
